import { useCallback, useEffect, useState } from 'react';
import { Stethoscope, Plus, FileText, ChevronRight, Loader2 } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { useVertical } from '../../../../context/VerticalContext';
import { DiagnosticModal, type DiagnosticContext, type Finding } from './DiagnosticModal';
import type { EditableLineItem } from '../../../../components/ui/LineItemsEditor';

interface ReportRow {
    id: string;
    status: 'in_progress' | 'complete' | 'quoted';
    customer_name: string | null;
    vehicle_vrm: string | null;
    vehicle_desc: string | null;
    summary: string | null;
    findings: Finding[];
    line_items: EditableLineItem[] | null;
    customer_id: string | null;
    vehicle_id: string | null;
    appointment_id: string | null;
    quote_id: string | null;
    created_at: string;
}

const STATUS_META: Record<ReportRow['status'], { label: string; className: string }> = {
    in_progress: { label: 'In progress', className: 'fd-live' },
    complete: { label: 'Complete', className: 'bg-card text-fg-3 border-line' },
    quoted: { label: 'Quoted', className: 'bg-success/10 text-success border-success/20' },
};

/**
 * Diagnostics tab — lists a garage's diagnostic reports (they live forever as
 * vehicle history) and launches the capture modal for a new one or to edit an
 * existing one.
 */
export default function DiagnosticsPage() {
    const { activeOrgId } = useVertical();
    const [reports, setReports] = useState<ReportRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<DiagnosticContext | undefined>(undefined);

    const load = useCallback(async () => {
        if (!activeOrgId) return;
        setLoading(true);
        const { data, error } = await supabase
            .from('diagnostic_reports')
            .select('*')
            .eq('org_id', activeOrgId)
            .order('created_at', { ascending: false })
            .limit(100);
        if (!error) setReports((data as ReportRow[]) ?? []);
        setLoading(false);
    }, [activeOrgId]);

    useEffect(() => {
        load();
    }, [load]);

    const openNew = () => {
        setEditing(undefined);
        setModalOpen(true);
    };

    const openReport = (r: ReportRow) => {
        setEditing({
            reportId: r.id,
            customerId: r.customer_id ?? undefined,
            vehicleId: r.vehicle_id ?? undefined,
            appointmentId: r.appointment_id ?? undefined,
            customerName: r.customer_name ?? undefined,
            vehicleVrm: r.vehicle_vrm ?? undefined,
            vehicleDesc: r.vehicle_desc ?? undefined,
            findings: r.findings ?? [],
            lineItems: r.line_items ?? [],
            summary: r.summary ?? undefined,
            status: r.status,
            createdAt: r.created_at,
            quoteId: r.quote_id,
        });
        setModalOpen(true);
    };

    return (
        <div className="flex-1 flex flex-col">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2 text-fg-3">
                    <Stethoscope className="w-5 h-5 text-gold" />
                    <span className="text-sm">Inspect a vehicle system-by-system, then turn findings into a quote.</span>
                </div>
                <button
                    onClick={openNew}
                    className="flex items-center justify-center gap-2 h-10 px-5 rounded-lg font-semibold font-display text-sm fd-btn-primary transition-colors whitespace-nowrap"
                >
                    <Plus className="w-4 h-4" />
                    New Diagnostic
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 text-gold animate-spin" />
                </div>
            ) : reports.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-16 gap-3">
                    <Stethoscope className="w-10 h-10 text-fg-4" strokeWidth={1.5} />
                    <p className="text-fg-2">No diagnostics yet.</p>
                    <p className="text-sm text-fg-3 max-w-sm">
                        Start one when a vehicle comes in for a health check — findings become a quote in one tap.
                    </p>
                    <button
                        onClick={openNew}
                        className="mt-2 flex items-center gap-2 h-10 px-5 rounded-lg font-semibold font-display text-sm fd-btn-primary transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        New Diagnostic
                    </button>
                </div>
            ) : (
                <div className="space-y-2 w-full overflow-y-auto pr-1 pb-10">
                    {reports.map((r) => {
                        const meta = STATUS_META[r.status];
                        const critical = (r.findings ?? []).filter((f) => f.severity === 'critical').length;
                        return (
                            <button
                                key={r.id}
                                onClick={() => openReport(r)}
                                className="w-full flex items-center gap-4 p-4 rounded-xl bg-card border border-line hover:border-gold transition-colors text-left"
                            >
                                {r.vehicle_vrm && (
                                    <span
                                        className="inline-block px-1.5 py-0.5 rounded-sm font-mono text-[11px] font-bold tracking-wider flex-shrink-0"
                                        style={{ backgroundColor: 'var(--fd-plate)', color: 'var(--fd-plate-ink)' }}
                                    >
                                        {r.vehicle_vrm}
                                    </span>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-fg truncate">
                                        {r.customer_name || 'Walk-in'}
                                    </p>
                                    <p className="text-xs text-fg-3 truncate">
                                        {r.vehicle_desc || '—'} · {(r.findings ?? []).length} finding
                                        {(r.findings ?? []).length === 1 ? '' : 's'}
                                        {critical > 0 && (
                                            <span className="text-danger"> · {critical} critical</span>
                                        )}
                                    </p>
                                </div>
                                <span className="text-[11px] text-fg-4 font-mono hidden sm:block">
                                    {new Date(r.created_at).toLocaleDateString('en-GB')}
                                </span>
                                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${meta.className}`}>
                                    {r.status === 'quoted' ? <FileText className="w-3 h-3 inline mr-1" /> : null}
                                    {meta.label}
                                </span>
                                <ChevronRight className="w-4 h-4 text-fg-4 flex-shrink-0" />
                            </button>
                        );
                    })}
                </div>
            )}

            <DiagnosticModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSaved={load}
                context={editing}
            />
        </div>
    );
}
